import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { IntegrationsService } from 'app/integrations/integration.service';
import { IntegrationPlatformType } from 'app/integrations/models/integration.model';
import { IntegrationPlatformSpecificsFactory } from 'app/integrations/integration-platform-specifics.factory';
import { AmplitudeService } from 'app/shared/services/amplitude.service';

const errorsToNavigation = new Map([
  ['INTEGRATION_EXISTS', '/dashboard'],
  ['ROLE_INVALID', '/integrations/error/pipedrive/invalid_role'],
  ['PIPEDRIVE_API_ERROR', '/integrations/error/pipedrive/invalid_role'],
  ['ACCOUNT_LIMITS_EXCEEDED', '/integrations/error/common/account_limits_exceeded'],
  ['MULTIPLE_INTEGRATIONS_NOT_SUPPORTED', '/integrations/error/shopify/multiple_integrations_not_supported'],
  ['INVALID_TIMESTAMP', '/integrations/error/shopify/invalid_timestamp'],
  ['ALREADY_INTEGRATED_ON_ANOTHER_DOMAIN', '/integrations/error/common/already_integrated_on_another_domain']
]);

@Component({
  selector: 'cc-confirm-integration',
  templateUrl: 'confirm-integration.component.html',
  styleUrls: ['confirm-integration.component.scss'],
  encapsulation: ViewEncapsulation.Emulated
})
export class ConfirmIntegrationComponent implements OnInit {
  public integratedPlatformType: IntegrationPlatformType;
  public installationInProgress: boolean;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private integrationsService: IntegrationsService,
    private specificsFactory: IntegrationPlatformSpecificsFactory,
    private amplitudeService: AmplitudeService
  ) {}

  ngOnInit() {
    this.integratedPlatformType = this.route.snapshot.paramMap
      .get('integratedPlatformType')
      .toUpperCase() as IntegrationPlatformType;

    const params = this.prepareParamsForBackendConfirm();

    this.installationInProgress = true;
    this.logAmplitudeEvent('Panel:Integrations:Confirm: Init');
    this.integrationsService.confirmIntegration(this.integratedPlatformType, params).subscribe(
      () => {
        this.logAmplitudeEvent('Panel:Integrations:Confirm: Success');
        this.installationInProgress = false;
      },
      errorResponse => {
        this.handleConfirmIntegrationError(errorResponse);
      }
    );
  }

  private prepareParamsForBackendConfirm() {
    const { queryParams } = this.route.snapshot;
    const params = {};
    Object.keys(queryParams).forEach(function(queryParamsKey) {
      params[queryParamsKey] = queryParams[queryParamsKey];
    });

    if (this.route.snapshot.paramMap.has('licenseID')) {
      params['licenseID'] = this.route.snapshot.paramMap.get('licenseID');
    }
    return params;
  }

  import() {
    this.logAmplitudeEvent('Panel:Integrations:Confirm: Import start');
    this.integrationsService.importOldContacts(this.integratedPlatformType).subscribe(() => this.skip());
  }

  skip() {
    this.logAmplitudeEvent('Panel:Integrations:Confirm: Skipping import');
    const path = this.integratedPlatformType.toString().toLocaleLowerCase();
    this.router.navigateByUrl(`/integrations/installed/${path}`);
  }

  allowsImport() {
    return this.specificsFactory.getSpecifics(this.integratedPlatformType).allowsImport;
  }

  private handleConfirmIntegrationError(errorResponse) {
    this.installationInProgress = false;
    const isError = errorResponse.error && errorResponse.error.length;
    if (!isError) {
      return;
    }

    const error = errorResponse.error[0];
    this.amplitudeService.logEvent('Panel:Integrations:Confirm: Error', {
      platform: this.integratedPlatformType,
      error
    });

    if (errorsToNavigation.has(error)) {
      this.router.navigateByUrl(errorsToNavigation.get(error) + '?type=' + this.integratedPlatformType);
    } else {
      this.goToDefaultErrorRout();
    }
  }

  private goToDefaultErrorRout() {
    this.router.navigate([
      'integrations',
      'error',
      this.integratedPlatformType.toString().toLocaleLowerCase(),
      'error'
    ]);
  }

  private logAmplitudeEvent(event: string) {
    this.amplitudeService.logEvent(event, {
      platform: this.integratedPlatformType
    });
  }
}
